import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Search, Trash2, AlertTriangle, RefreshCw, FileOutput, Info, Truck, Users, Pencil, History } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { toast } from 'sonner';
import apiClient from '@/services/apiClient';

const OutwardEntryManagement = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [searchType, setSearchType] = useState('document_barcode');
  const [searchResults, setSearchResults] = useState(null);
  const [isSearching, setIsSearching] = useState(false);
  
  // Entry list
  const [entries, setEntries] = useState([]);
  const [totalEntries, setTotalEntries] = useState(0);
  const [isLoadingEntries, setIsLoadingEntries] = useState(false);
  const [listSearchTerm, setListSearchTerm] = useState('');
  const [entryTypeFilter, setEntryTypeFilter] = useState('');
  const [page, setPage] = useState(0);
  const limit = 20;
  
  // Delete dialog
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deleteSearchTerm, setDeleteSearchTerm] = useState('');
  const [deleteSearchType, setDeleteSearchType] = useState('document_barcode');
  const [deleteEntryType, setDeleteEntryType] = useState('');
  const [confirmText, setConfirmText] = useState('');
  const [deleteReason, setDeleteReason] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);

  // Edit dialog
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editEntry, setEditEntry] = useState(null);
  const [editFields, setEditFields] = useState({ transport_name: '', vehicle_number: '', reason: '' });
  const [isSaving, setIsSaving] = useState(false);

  // Audit log dialog
  const [showAuditDialog, setShowAuditDialog] = useState(false);
  const [auditEntryId, setAuditEntryId] = useState('');
  const [auditRows, setAuditRows] = useState([]);
  const [isLoadingAudit, setIsLoadingAudit] = useState(false);

  const fetchEntries = async () => {
    setIsLoadingEntries(true);
    try {
      const params = new URLSearchParams();
      params.append('skip', page * limit);
      params.append('limit', limit);
      if (listSearchTerm) params.append('search', listSearchTerm);
      if (entryTypeFilter) params.append('entry_type', entryTypeFilter);
      
      const response = await apiClient.get(`/admin/outward-entries/list?${params}`);
      setEntries(response.data.entries || []);
      setTotalEntries(response.data.total || 0);
    } catch (error) {
      console.error('Error fetching entries:', error);
      toast.error('Failed to load Outward entries');
    } finally {
      setIsLoadingEntries(false);
    }
  };

  useEffect(() => {
    fetchEntries();
  }, [page, entryTypeFilter]);

  const handleSearch = async () => {
    if (!searchTerm.trim()) {
      toast.error('Please enter a search term');
      return;
    }
    
    setIsSearching(true);
    setSearchResults(null);
    try {
      const param = searchType === 'document_barcode' ? 'document_barcode' : 'entry_id';
      const response = await apiClient.get(`/admin/outward-entries/search?${param}=${encodeURIComponent(searchTerm.trim())}`);
      setSearchResults(response.data);
      
      if (!response.data.found) {
        toast.info('No Outward data found for this search term');
      }
    } catch (error) {
      console.error('Error searching:', error);
      toast.error(error.response?.data?.detail || 'Search failed');
    } finally {
      setIsSearching(false);
    }
  };

  const openDeleteDialog = (term, type = 'document_barcode', entryType = '') => {
    setDeleteSearchTerm(term);
    setDeleteSearchType(type);
    setDeleteEntryType(entryType);
    setConfirmText('');
    setDeleteReason('');
    setShowDeleteDialog(true);
  };

  const handleDelete = async () => {
    if (confirmText !== 'DELETE') {
      toast.error('Please type DELETE to confirm');
      return;
    }
    
    setIsDeleting(true);
    try {
      const param = deleteSearchType === 'document_barcode' ? 'document_barcode' : 'entry_id';
      let url = `/admin/outward-entries?${param}=${encodeURIComponent(deleteSearchTerm)}`;
      if (deleteEntryType) {
        url += `&entry_type=${deleteEntryType}`;
      }
      if (deleteReason.trim()) {
        url += `&reason=${encodeURIComponent(deleteReason.trim())}`;
      }
      
      const response = await apiClient.delete(url);
      
      toast.success(`Soft-deleted ${response.data.total_records_deleted} record(s) — recoverable from audit log`);
      setShowDeleteDialog(false);
      setSearchResults(null);
      setSearchTerm('');
      fetchEntries();
    } catch (error) {
      console.error('Error deleting:', error);
      toast.error(error.response?.data?.detail || 'Delete failed');
    } finally {
      setIsDeleting(false);
    }
  };

  const openEditDialog = (entry) => {
    setEditEntry(entry);
    setEditFields({
      transport_name: entry.transport_name || '',
      vehicle_number: entry.vehicle_number || '',
      reason: '',
    });
    setShowEditDialog(true);
  };

  const handleSaveEdit = async () => {
    if (!editEntry) return;
    const t = (editFields.transport_name || '').trim();
    const v = (editFields.vehicle_number || '').trim();
    if (!t && !v) {
      toast.error('Provide at least Transport or Vehicle to update');
      return;
    }
    setIsSaving(true);
    try {
      const payload = { reason: editFields.reason || '' };
      if (t !== (editEntry.transport_name || '')) payload.transport_name = t;
      if (v !== (editEntry.vehicle_number || '')) payload.vehicle_number = v;
      await apiClient.patch(
        `/admin/outward-entries/${encodeURIComponent(editEntry.entry_id)}`,
        payload,
      );
      toast.success('Outward entry updated');
      setShowEditDialog(false);
      fetchEntries();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Update failed');
    } finally {
      setIsSaving(false);
    }
  };

  const openAuditDialog = async (entryId) => {
    setAuditEntryId(entryId);
    setAuditRows([]);
    setShowAuditDialog(true);
    setIsLoadingAudit(true);
    try {
      const res = await apiClient.get(`/admin/outward-entries/${encodeURIComponent(entryId)}/audit-log`);
      setAuditRows(res.data.audit_log || []);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to load audit log');
    } finally {
      setIsLoadingAudit(false);
    }
  };

  const handleListSearch = () => {
    setPage(0);
    fetchEntries();
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleString('en-IN', {
      day: '2-digit',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getEntryTypeBadge = (type) => {
    const badges = {
      'job_worker': 'bg-orange-100 text-orange-800',
      'customer': 'bg-blue-100 text-blue-800',
      'internal_transfer': 'bg-purple-100 text-purple-800'
    };
    return badges[type] || 'bg-gray-100 text-gray-800';
  };

  const getEntryTypeLabel = (type) => {
    const labels = {
      'job_worker': 'Job Worker',
      'customer': 'Customer',
      'internal_transfer': 'Internal Transfer'
    };
    return labels[type] || type || '-';
  };

  return (
    <div className="min-h-screen bg-[#FFFBF5]" data-testid="outward-entry-management">
      <header className="bg-[#1A1F2E] text-[#F9FAFB] shadow-lg">
        <div className="max-w-7xl mx-auto px-4 md:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" onClick={() => navigate('/admin/dashboard')} className="text-[#F9FAFB] hover:bg-white/10">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-xl font-bold font-display flex items-center gap-2">
                <FileOutput className="w-6 h-6" />
                Outward Entry Management
              </h1>
              <p className="text-sm text-[#E8DCC8]">Delete Outward entries (Job Worker, Customer, Internal Transfer)</p>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 md:px-6 py-8 space-y-6">
        {/* Search */}
        <Card className="border-[#E5DFD0]">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-[#1A1F2E]">
              <Search className="w-5 h-5" />
              Search Entry
            </CardTitle>
            <CardDescription>
              Search by document barcode or entry ID to view and delete Outward data
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex gap-3 flex-wrap">
              <select
                value={searchType}
                onChange={(e) => setSearchType(e.target.value)}
                className="px-3 py-2 border rounded-md bg-white"
              >
                <option value="document_barcode">Document Barcode</option>
                <option value="entry_id">Entry ID</option>
              </select>
              <Input
                placeholder={searchType === 'document_barcode' ? 'Enter Document Barcode (e.g., DOC000001/25-26)' : 'Enter Entry ID'}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                className="flex-1 min-w-[300px]"
                data-testid="search-input"
              />
              <Button 
                onClick={handleSearch} 
                disabled={isSearching}
                className="bg-rose-600 hover:bg-rose-700"
                data-testid="search-btn"
              >
                {isSearching ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
                <span className="ml-2">Search</span>
              </Button>
            </div>

            {/* Search Results */}
            {searchResults && (
              <div className="mt-6 space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="font-semibold text-[#1A1F2E]">
                    Results for: {searchResults.search_term}
                  </h3>
                  {searchResults.found && (
                    <Button 
                      variant="destructive" 
                      size="sm"
                      onClick={() => openDeleteDialog(searchResults.search_term, searchResults.search_type)}
                      data-testid="delete-btn"
                    >
                      <Trash2 className="w-4 h-4 mr-2" />
                      Delete All Data
                    </Button>
                  )}
                </div>

                {searchResults.found ? (
                  <div className="space-y-4">
                    {/* Outward Entries */}
                    <Card className="border-rose-300 bg-rose-50">
                      <CardContent className="p-4">
                        <div className="flex items-center gap-2 mb-2">
                          <FileOutput className="w-5 h-5 text-rose-600" />
                          <h4 className="font-semibold">Outward Entries</h4>
                        </div>
                        {searchResults.data.outward_entries ? (
                          <div>
                            <p className="text-2xl font-bold text-rose-600">{searchResults.data.outward_entries.count}</p>
                          </div>
                        ) : (
                          <p className="text-gray-500 text-sm">No data</p>
                        )}
                      </CardContent>
                    </Card>

                    {/* Type Breakdown */}
                    {searchResults.data.type_breakdown && (
                      <div className="grid grid-cols-3 gap-4">
                        <Card className="border-orange-200 bg-orange-50">
                          <CardContent className="p-4 text-center">
                            <Truck className="w-6 h-6 text-orange-600 mx-auto mb-2" />
                            <p className="text-sm text-gray-600">Job Worker</p>
                            <p className="text-xl font-bold text-orange-600">{searchResults.data.type_breakdown.job_worker}</p>
                          </CardContent>
                        </Card>
                        <Card className="border-blue-200 bg-blue-50">
                          <CardContent className="p-4 text-center">
                            <Users className="w-6 h-6 text-blue-600 mx-auto mb-2" />
                            <p className="text-sm text-gray-600">Customer</p>
                            <p className="text-xl font-bold text-blue-600">{searchResults.data.type_breakdown.customer}</p>
                          </CardContent>
                        </Card>
                        <Card className="border-purple-200 bg-purple-50">
                          <CardContent className="p-4 text-center">
                            <FileOutput className="w-6 h-6 text-purple-600 mx-auto mb-2" />
                            <p className="text-sm text-gray-600">Internal Transfer</p>
                            <p className="text-xl font-bold text-purple-600">{searchResults.data.type_breakdown.internal_transfer}</p>
                          </CardContent>
                        </Card>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="p-4 bg-gray-100 rounded-lg text-center">
                    <Info className="w-8 h-8 mx-auto text-gray-400 mb-2" />
                    <p className="text-gray-600">No Outward data found</p>
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Entry List */}
        <Card className="border-[#E5DFD0]">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-[#1A1F2E]">Recent Outward Entries</CardTitle>
                <CardDescription>Latest outward gate entries</CardDescription>
              </div>
              <Button variant="outline" size="sm" onClick={fetchEntries} disabled={isLoadingEntries}>
                <RefreshCw className={`w-4 h-4 mr-2 ${isLoadingEntries ? 'animate-spin' : ''}`} />
                Refresh
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="flex gap-3 mb-4 flex-wrap">
              <select
                value={entryTypeFilter}
                onChange={(e) => { setEntryTypeFilter(e.target.value); setPage(0); }}
                className="px-3 py-2 border rounded-md bg-white"
              >
                <option value="">All Types</option>
                <option value="job_worker">Job Worker</option>
                <option value="customer">Customer</option>
                <option value="internal_transfer">Internal Transfer</option>
              </select>
              <Input
                placeholder="Search by entry ID, document barcode, destination..."
                value={listSearchTerm}
                onChange={(e) => setListSearchTerm(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleListSearch()}
                className="flex-1"
              />
              <Button onClick={handleListSearch} variant="outline">
                <Search className="w-4 h-4" />
              </Button>
            </div>

            <div className="rounded-md border overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Entry ID</TableHead>
                    <TableHead>Document Barcode</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Destination</TableHead>
                    <TableHead>Vehicle</TableHead>
                    <TableHead>Created</TableHead>
                    <TableHead className="text-center">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {isLoadingEntries ? (
                    <TableRow>
                      <TableCell colSpan={7} className="text-center py-8">
                        <RefreshCw className="w-6 h-6 animate-spin mx-auto text-rose-600" />
                      </TableCell>
                    </TableRow>
                  ) : entries.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} className="text-center py-8 text-gray-500">
                        No entries found
                      </TableCell>
                    </TableRow>
                  ) : (
                    entries.map((entry, idx) => (
                      <TableRow key={`${entry.entry_id}-${idx}`}>
                        <TableCell className="font-mono text-sm">{entry.entry_id || '-'}</TableCell>
                        <TableCell className="font-mono text-sm">{entry.document_barcode || '-'}</TableCell>
                        <TableCell>
                          <span className={`px-2 py-0.5 rounded text-xs ${getEntryTypeBadge(entry.entry_type)}`}>
                            {getEntryTypeLabel(entry.entry_type)}
                          </span>
                        </TableCell>
                        <TableCell className="text-sm">{entry.destination || '-'}</TableCell>
                        <TableCell className="text-sm">{entry.vehicle_number || '-'}</TableCell>
                        <TableCell className="text-sm text-gray-600">{formatDate(entry.created_at)}</TableCell>
                        <TableCell className="text-center">
                          <div className="flex items-center justify-center gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                              onClick={() => openEditDialog(entry)}
                              data-testid={`edit-btn-${entry.entry_id}`}
                              title="Edit transporter/vehicle"
                            >
                              <Pencil className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-gray-600 hover:text-gray-800 hover:bg-gray-100"
                              onClick={() => openAuditDialog(entry.entry_id)}
                              data-testid={`audit-btn-${entry.entry_id}`}
                              title="View audit log"
                            >
                              <History className="w-4 h-4" />
                            </Button>
                            <Button 
                              variant="ghost" 
                              size="sm"
                              className="text-red-600 hover:text-red-700 hover:bg-red-50"
                              onClick={() => openDeleteDialog(entry.entry_id, 'entry_id', entry.entry_type)}
                              data-testid={`delete-btn-${entry.entry_id}`}
                              title="Soft-delete entry"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>

            {totalEntries > limit && (
              <div className="flex items-center justify-between mt-4">
                <p className="text-sm text-gray-600">
                  Showing {page * limit + 1} - {Math.min((page + 1) * limit, totalEntries)} of {totalEntries}
                </p>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={() => setPage(p => Math.max(0, p - 1))} disabled={page === 0}>
                    Previous
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => setPage(p => p + 1)} disabled={(page + 1) * limit >= totalEntries}>
                    Next
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </main>

      {/* Delete Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-700">
              <AlertTriangle className="w-5 h-5" />
              Delete Outward Entry Data
            </DialogTitle>
            <DialogDescription>
              This will permanently delete Outward data for:
              <span className="font-mono font-bold block mt-1">{deleteSearchTerm}</span>
              {deleteEntryType && (
                <span className="block mt-1 text-sm">Type filter: {getEntryTypeLabel(deleteEntryType)}</span>
              )}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-red-800">
                <strong>Warning:</strong> This action cannot be undone. All outward records 
                {deleteEntryType ? ` of type "${getEntryTypeLabel(deleteEntryType)}"` : ''} for this entry will be deleted.
              </p>
            </div>

            <div>
              <Label className="text-red-700">Type DELETE to confirm:</Label>
              <Input
                value={confirmText}
                onChange={(e) => setConfirmText(e.target.value)}
                placeholder="DELETE"
                className="mt-1 border-red-300 focus:border-red-500"
                data-testid="confirm-input"
              />
            </div>

            <div>
              <Label className="text-gray-700">Reason (optional, stored in audit log):</Label>
              <Input
                value={deleteReason}
                onChange={(e) => setDeleteReason(e.target.value)}
                placeholder="e.g. Wrong DC tagged at gate"
                className="mt-1"
                data-testid="delete-reason-input"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteDialog(false)}>Cancel</Button>
            <Button
              variant="destructive"
              onClick={handleDelete}
              disabled={isDeleting || confirmText !== 'DELETE'}
              data-testid="confirm-delete-btn"
            >
              {isDeleting ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Trash2 className="w-4 h-4 mr-2" />}
              Delete Data
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="max-w-md" data-testid="edit-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-blue-700">
              <Pencil className="w-5 h-5" />
              Edit Outward Entry
            </DialogTitle>
            <DialogDescription>
              Correct mis-keyed gate fields. Only Transport and Vehicle are editable.
              {editEntry && (
                <span className="block mt-1 font-mono text-xs">{editEntry.entry_id}</span>
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Transport Name</Label>
              <Input
                value={editFields.transport_name}
                onChange={(e) => setEditFields(f => ({ ...f, transport_name: e.target.value }))}
                placeholder="Transporter name"
                data-testid="edit-transport-input"
              />
            </div>
            <div>
              <Label>Vehicle Number</Label>
              <Input
                value={editFields.vehicle_number}
                onChange={(e) => setEditFields(f => ({ ...f, vehicle_number: e.target.value }))}
                placeholder="TN-12-AB-1234"
                className="font-mono"
                data-testid="edit-vehicle-input"
              />
            </div>
            <div>
              <Label className="text-gray-700">Reason (audit trail)</Label>
              <Input
                value={editFields.reason}
                onChange={(e) => setEditFields(f => ({ ...f, reason: e.target.value }))}
                placeholder="Why is this changing?"
                data-testid="edit-reason-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditDialog(false)}>Cancel</Button>
            <Button onClick={handleSaveEdit} disabled={isSaving} data-testid="confirm-edit-btn">
              {isSaving ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Pencil className="w-4 h-4 mr-2" />}
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Audit Log Dialog */}
      <Dialog open={showAuditDialog} onOpenChange={setShowAuditDialog}>
        <DialogContent className="max-w-3xl" data-testid="audit-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <History className="w-5 h-5 text-gray-700" />
              Audit Log — <span className="font-mono text-sm">{auditEntryId}</span>
            </DialogTitle>
            <DialogDescription>
              Every edit and soft-delete touching this entry, newest first.
            </DialogDescription>
          </DialogHeader>
          <div className="max-h-[55vh] overflow-auto border rounded">
            {isLoadingAudit ? (
              <div className="py-10 text-center text-sm text-gray-500">
                <RefreshCw className="w-5 h-5 animate-spin mx-auto" />
              </div>
            ) : auditRows.length === 0 ? (
              <div className="py-10 text-center text-sm text-gray-400">No audit events recorded yet.</div>
            ) : (
              <Table className="text-xs">
                <TableHeader className="sticky top-0 bg-gray-50">
                  <TableRow>
                    <TableHead>When</TableHead>
                    <TableHead>Who</TableHead>
                    <TableHead>Action</TableHead>
                    <TableHead>Reason</TableHead>
                    <TableHead>Before → After</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {auditRows.map((r, idx) => (
                    <TableRow key={idx} data-testid={`audit-row-${idx}`}>
                      <TableCell className="whitespace-nowrap">{(r.audit_at || '').replace('T', ' ').slice(0, 19)}</TableCell>
                      <TableCell className="text-gray-700">{r.audit_by || '-'}</TableCell>
                      <TableCell>
                        <span className={`px-2 py-0.5 rounded text-[10px] ${r.action === 'soft_delete' ? 'bg-red-100 text-red-800' : 'bg-blue-100 text-blue-800'}`}>
                          {r.action}
                        </span>
                      </TableCell>
                      <TableCell className="text-gray-600">{r.reason || '—'}</TableCell>
                      <TableCell className="font-mono text-[10px] text-gray-600">
                        {r.action === 'soft_delete' ? (
                          <span className="text-red-600">Entry removed (snapshot stored)</span>
                        ) : (
                          <div className="space-y-0.5">
                            {Object.keys(r.after || {}).map(k => (
                              <div key={k}>
                                <span className="text-gray-500">{k}:</span> {String((r.before || {})[k] ?? '∅')} → {String(r.after[k] ?? '∅')}
                              </div>
                            ))}
                          </div>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAuditDialog(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default OutwardEntryManagement;
